import { Stack } from '@evaneos/b2b-ui';
import { Button } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { addDays, isAfter } from 'date-fns';
import React, { SyntheticEvent, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';

import { FlightTakeoff } from '../../../design-system/Icons/FlightTakeoff';
import { Replay } from '../../../design-system/Icons/Replay';
import { Loader } from '../../../design-system/Loader';
import { Rating } from '../../../design-system/Rating';
import { RepeaterBadge } from '../../../design-system/RepeaterBadge/RepeaterBadge';
import { AT1AutoExperimentChip } from '../../../shared/components/ExperimentChip'; // [BB-EXP] - AT1 auto
import { Avatar } from '../../../resources/agent/components/Avatar';
import { DossierId, DossierOverview } from '../../../resources/dossier/types';
import { getDurationInHoursBetweenRequestDateAndToday } from '../../../resources/todo';
import { AllTranslationKeys } from '../../../shared/intl/types';
import { HeatChip } from '../../../shared/components/DossierQualification/HeatChip';
import { TagsTooltipChip } from '../../../shared/components/DossierQualification/TagsTooltipChip';

import { getToday } from '../../../shared/utilities/date';
import { useGetAgents, useCanDisplayAvatar } from '../../../resources/agent/hooks';
import { useTrackEventAmplitude } from '../../../shared/analytics';
import {
    ChangeStepMutationVariables,
    useChangeStepStatus,
} from '../../../resources/dossier/mutations';
import s from '../Process.module.css';

import styleProject from './Project.module.css';

type Props = {
    dossier: DossierOverview;
    isLatestUpdated?: boolean;
    onRetry?: (params: ChangeStepMutationVariables) => void;
    setLatestUpdated?: (dossierId: DossierId) => void;
    doNotDrag?: boolean;
    isDragOverlay?: boolean;
};

export const NewRequestDossierOverview = React.memo(
    // eslint-disable-next-line max-lines-per-function -- complex dossier card with drag-and-drop, rating, duplicate and retry logic
    ({
        dossier,
        doNotDrag,
        isLatestUpdated,
        onRetry,
        setLatestUpdated,
        isDragOverlay,
    }: Props): JSX.Element => {
        const { formatMessage } = useIntl();
        const { trackEvent } = useTrackEventAmplitude();

        const { data: agents } = useGetAgents();

        const {
            id,
            project,
            discussion: { lastAgentToTravellerMessageDate },
            task,
            step,
            allowedChangeStepsTo,
            agentId,
            rating,
            requestDate,
            isDuplicate,
            requesterIsRepeater,
            heatLevel,
            tags,
        } = dossier;

        const canDisplayAvatar = useCanDisplayAvatar(agentId);

        const { isPending, isError, stepInError } = useChangeStepStatus(id);

        const [isTooltipOpen, setTooltipOpen] = useState(false);

        const handleMouseEnter = () => setTooltipOpen(true);
        const handleMouseLeave = () => setTooltipOpen(false);

        const handleRetry = (e: SyntheticEvent<HTMLButtonElement>): void => {
            e.preventDefault();
            if (isError && stepInError) {
                onRetry?.({
                    dossierId: id,
                    nextStep: stepInError,
                });
                setLatestUpdated?.(id);
            }
        };

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: id,
            data: {
                dossierId: id,
                currentStep: step,
                dossierCanChangeStepTo: allowedChangeStepsTo,
            },
            disabled: isError || doNotDrag,
        });

        const currentDate = getToday();
        const isDueDateLate: boolean = isAfter(currentDate, addDays(task.dueDate, 1));

        const isNoAT1NewDossier = lastAgentToTravellerMessageDate === null;

        let durationInHoursBetweenRequestDateAndToday = 0;
        if (isNoAT1NewDossier) {
            durationInHoursBetweenRequestDateAndToday =
                getDurationInHoursBetweenRequestDateAndToday(currentDate, requestDate);
        }
        const isRequestDateLate = durationInHoursBetweenRequestDateAndToday > 24;

        const renderTaskDueDate = isNoAT1NewDossier ? (
            <div
                className={cx(s.task, {
                    [s.lateTask]: isRequestDateLate,
                    [s.todayTask]: !isRequestDateLate,
                })}
                data-testid={`duration-between-request-date-and-today-${id}`}
            >
                <p className={cx('badge', s.taskDueDate)}>
                    <FormattedNumber value={durationInHoursBetweenRequestDateAndToday} />h
                </p>
            </div>
        ) : (
            <div
                className={cx(s.task, {
                    [s.lateTask]: isDueDateLate,
                    [s.todayTask]: !isDueDateLate,
                })}
                data-testid={`due-date-${id}`}
            >
                <p className={cx('badge', s.taskDueDate)}>
                    <FormattedDate value={task.dueDate} day="2-digit" month="short" />
                </p>
            </div>
        );

        return (
            <div
                id={id}
                key={id}
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    opacity: isDragging && !isDragOverlay ? 0.3 : 1,
                    marginBottom: '4px',
                }}
                data-testid="new-request-dossier-overview"
            >
                <Link
                    data-testid={`dossier-overview-id-${id}`}
                    to={{ pathname: `/dossier/${id}/infos` }}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.newFileClicked({
                                ...defaultProperties,
                                request_id: parseInt(dossier.id),
                                source: 'pipeline',
                                is_overdue: isRequestDateLate,
                            });
                        });
                    }}
                >
                    <div
                        className={cx(s.newRequestDossierContainer, {
                            [s.latestUpdated]: isLatestUpdated,
                            [s.cardDuplicate]: isDuplicate,
                        })}
                    >
                        {isPending && (
                            <div className={s.overlay}>
                                <Loader />
                            </div>
                        )}
                        {isError && (
                            <div className={s.overlay}>
                                <Button
                                    priority="secondary"
                                    onClick={handleRetry}
                                    className={s.retryButton}
                                    data-testid="retry-button"
                                >
                                    <Replay color="var(--ink)" />
                                    <FormattedMessage id={`global.retry`} />
                                </Button>
                            </div>
                        )}
                        <div className={s.newRequestWithDateAndTask}>
                            <HeatChip heatLevel={heatLevel} isSelected />
                            <TagsTooltipChip tags={tags} isTooltipOpen={isTooltipOpen} />
                            {/* [BB-EXP] - AT1 auto: Display chip for AT1 auto dossiers */}
                            {dossier.isAt1Auto ? (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <AT1AutoExperimentChip size="small" />
                                </div>
                            ) : null}
                            {!isNoAT1NewDossier && (
                                <div className={cx(s.task, s.shrinkable)}>
                                    <p className={cx('badge', s.task, s.taskDefinition)}>
                                        <span>
                                            <FormattedMessage
                                                id={
                                                    `tasks.${task.definition}.name` as AllTranslationKeys
                                                }
                                            />
                                        </span>
                                    </p>
                                </div>
                            )}
                            {renderTaskDueDate}
                            {isNoAT1NewDossier && (
                                <div className={s.taskNewRequest}>
                                    <p className={cx('badge', s.taskNewRequest, s.taskDefinition)}>
                                        <span>New Request</span>
                                    </p>
                                </div>
                            )}
                            {requesterIsRepeater && (
                                <div className={s.rightTopCorner}>
                                    <RepeaterBadge noMargin />
                                </div>
                            )}
                        </div>
                        <div>
                            <p
                                className={cx('badge', styleProject.projectRequester, {
                                    [styleProject.duplicate]: isDuplicate,
                                })}
                            >
                                <span className={styleProject.requester}>
                                    <span>{project.requesterName}</span>

                                    {/* eslint-disable-next-line sonarjs/jsx-no-leaked-render -- rating is a number, falsy 0 is intentionally excluded */}
                                    {rating && !isDuplicate && (
                                        <Rating className={styleProject.rating} value={rating} />
                                    )}
                                    {isDuplicate &&
                                        ' - ' +
                                            formatMessage({
                                                id: 'dossier.duplicate.mark',
                                            })}
                                </span>
                            </p>
                            <div className={s.newRequestArrivalDateContainer}>
                                <Stack direction="row">
                                    <FlightTakeoff
                                        className={styleProject.projectIcon}
                                        color={'var(--ink-light)'}
                                        width="20px"
                                        height="20px"
                                    />
                                    {project.expectedArrivalDate && (
                                        <p className="annotation">
                                            <FormattedDate
                                                value={project.expectedArrivalDate}
                                                day={project.arrivalDate ? '2-digit' : undefined}
                                                month="short"
                                                year="numeric"
                                            />
                                        </p>
                                    )}
                                    {!project.expectedArrivalDate && (
                                        <div className={styleProject.emptyInformation}>——</div>
                                    )}
                                </Stack>
                            </div>
                            <div className={s.rightBottomCorner}>
                                {canDisplayAvatar && agents && (
                                    <Avatar
                                        name={agents[agentId].name}
                                        photoUrl={agents[agentId].photoUrl}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        );
    },
);
